import { Component, Inject, inject, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {BtbRolesCheckService} from "../../components/rolesCheck/btb-roles-check.service";
import { FilterField, FilterType } from '../../components/generic-list/filter-field.model';
import { TranslateService } from '@ngx-translate/core';
import { Auth } from 'aws-amplify';
import { AmplifyUser } from '@aws-amplify/ui';
import { AuthService } from '../../services/auth.service';
import { AWSLambda } from '../../AWSLambda.service';
import { firstValueFrom } from 'rxjs';
import { SpinnerService } from '../../components/spinner/spinner.service';
import { MatDialog } from '@angular/material/dialog';
import { AddInvoiceDialogComponent } from './add-invoice-dialog.component';

// BTB Components
import {
  BtbDataService,
  PartnerInfo,
  TabConfig,
  Invoice,
  InvoiceAction,
  BtbInvoicesListComponent,
  BtbTransactionsListComponent
} from '../../components/btb';

@Component({
  selector: 'app-dashboard-btb',
  templateUrl: './Dashboard.component.html',
  styles: [
    `
      .loading-mask {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(
          0,
          0,
          0,
          0.5
        );
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
      }
    `,
  ],
})
export class btbDashboardComponent implements OnInit {
  @ViewChild(BtbInvoicesListComponent) invoicesListComponent!: BtbInvoicesListComponent;
  
  @ViewChild(BtbTransactionsListComponent)
  transactionsListComponent!: BtbTransactionsListComponent;
  
  rolesCheck = inject(BtbRolesCheckService);
  canAccess = false;
  private translate = inject(TranslateService);
  private auth = inject(AuthService);
  private lambda = inject(AWSLambda);
  public spinnerService = inject(SpinnerService);
  private snackBar = inject(MatSnackBar);
  private btbDataService = inject(BtbDataService);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);

  user!: AmplifyUser;
  userName = '';
  userPhone = '';

  partnerId = '';

  totalBilling = 0;
  pendingTotal = 0;
  numClients = 0;
  allInvoices: any[] = [];
  condicionesComision: string[] = [];

  filterFields: FilterField[] = [];
  filterTransactionsFields: FilterField[] = [];

  filteredCounts = {
    facturasLiquidadas: 0,
    facturasPendientes: 0,
    userTransactions: 0,
  };

  activeTab: string = 'transactions';

  userTransactions: any[] = [];

  partnerInfo: PartnerInfo = {
    numClients: 0,
    pendingTotal: 0,
    totalBilling: 0
  };

  tabs: TabConfig[] = [
    {
      id: 'transactions',
      label: 'Transacciones',
      icon: 'account_balance_wallet',
      translationKey: 'BTB.DASHBOARD.TABS.TRANSACTIONS'
    },
    {
      id: 'invoices',
      label: 'Facturas',
      icon: 'receipt',
      translationKey: 'BTB.DASHBOARD.TABS.INVOICES'
    },
    {
      id: 'commission',
      label: 'Comisiones',
      icon: 'percent',
      translationKey: 'BTB.DASHBOARD.TABS.COMMISSION'
    }
  ];

  async ngOnInit() {
    this.canAccess = await this.rolesCheck.checkAccess();

    if (this.canAccess) {
      this.spinnerService.show('btb-dashboard');

      try {
        await this.loadUserData();
        await this.loadAllData();
        
        // Las transacciones se cargarán automáticamente desde el componente hijo
        this.userTransactions = [];

        this.updatePartnerInfo();

      } catch (error) {
        console.error('Error initializing dashboard:', error);
      } finally {
        this.spinnerService.hide('btb-dashboard');
      }
    }
  }

  private async loadUserData() {
    try {
      await this.auth.refresh();
      this.user = await Auth.currentAuthenticatedUser();
      this.userName = this.user.attributes?.['name'] || '';
      this.userPhone = this.user.attributes?.['phone_number'] || '';
      this.partnerId = this.user.attributes?.['custom:btbPartnerId'] || '';
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  }

  private async loadAllData() {
    try {
      const [numClients, allInvoices, conditions, billingTotals] = await Promise.all([
        this.btbDataService.loadClientCount(),
        this.btbDataService.loadAllInvoices(),
        this.btbDataService.loadConditions(),
        this.btbDataService.loadBillingTotals()
      ]);

      this.numClients = numClients;
      this.allInvoices = [...allInvoices];
      this.condicionesComision = [...conditions];
      this.totalBilling = billingTotals.totalBilling;
      this.pendingTotal = billingTotals.pendingBilling;

    } catch (error) {
      console.error('Error loading data:', error);
    }
  }

  private async initializeFilterFields() {
    try {
      const filters = await this.btbDataService.initializeFilterFields(this.allInvoices, this.userTransactions);

      this.filterFields = filters.filterFields;
      this.filterTransactionsFields = filters.filterTransactionsFields;

    } catch (error) {
      console.error('Error initializing filter fields:', error);
    }
  }

  private updatePartnerInfo() {
    this.partnerInfo = {
      name: this.userName,
      phone: this.userPhone,
      partnerId: this.partnerId,
      numClients: this.numClients,
      pendingTotal: this.pendingTotal,
      totalBilling: this.totalBilling
    };
  }

  onTabChange(tabId: string) {
    this.activeTab = tabId;
  }

  onInvoiceAction(action: InvoiceAction) {
    switch (action.action) {
      case 'download':
        this.downloadFactura(action.invoice);
        break;
      case 'confirm_invoice':
        this.confirmInvoice(action.invoice);
        break;
      case 'cancel_invoice':
        this.cancelInvoice(action.invoice);
        break;
      default:
        console.log('Action not implemented:', action.action);
    }
  }

  onFilteredCountChange(data: {type: string, count: number}) {
    this.filteredCounts[data.type as keyof typeof this.filteredCounts] = data.count;
  }

  onTransactionsFilteredCountChange(count: number) {
    this.filteredCounts.userTransactions = count;
    // Forzar detección de cambios para evitar ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  async onBuscarPorFechas(fechas: {fechaini: string, fechafin: string}) {
    const start = this.formatDateStart(fechas.fechaini);
    const end = this.formatDateEnd(fechas.fechafin);

    // No limpiar las transacciones inmediatamente para mantener el contador
    // Las transacciones se actualizarán cuando lleguen los nuevos datos

    try {
      const transactions = await this.btbDataService.getuserRefTransactions(
        this.user.username || '',
        start,
        end
      );
      // Crear nueva referencia para forzar detección de cambios
      this.userTransactions = [...transactions];
      
      await this.initializeFilterFields();
      
      // Actualizar las fechas de la última búsqueda exitosa
      if (this.transactionsListComponent) {
        this.transactionsListComponent.updateLastSearchDates(fechas.fechaini, fechas.fechafin);
      }
      
      // Forzar detección de cambios después de cargar los datos
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error buscando por fechas', error);
      // Mostrar mensaje de error al usuario
      this.snackBar.open('Error al cargar las transacciones. Por favor, inténtelo de nuevo.', 'Cerrar', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      // Asegurar que se limpia en caso de error con nueva referencia
      this.userTransactions = [];
      this.cdr.detectChanges();
    } finally {
      if (this.transactionsListComponent) {
        this.transactionsListComponent.stopLoading();
      }
    }
  }

  private formatDateStart(dateStr: string): string {
    if (dateStr.includes('T')) return dateStr;
    return `${dateStr}T00:00:00Z`;
  }

  private formatDateEnd(dateStr: string): string {
    if (dateStr.includes('T')) return dateStr;
    return `${dateStr}T23:59:59Z`;
  }

  addInvoice() {
    const ref = this.dialog.open(AddInvoiceDialogComponent, {
      width: '520px',
      data: {},
      hasBackdrop: true,
      disableClose: true,
      autoFocus: false
    });
    ref.afterClosed().subscribe(async (result) => {
      if (result && result.success) {
        await this.loadAllData();
        await this.initializeFilterFields();
      }
    });
  }

  async downloadFactura(item: any) {
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: {
          method: 'downloadInvoice',
          invoiceId: item.id
        }
      });

      if (response.downloadUrl) {
        const fetchResponse = await fetch(response.downloadUrl);
        const blob = await fetchResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.fileName || `factura_${item.id}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('No se recibió URL de descarga');
      }

    } catch (error) {
      console.error('Error downloading invoice:', error);
      this.snackBar.open('Error al descargar la factura', 'Cerrar', { duration: 3000 });
    } finally {
      // Notificar al componente de listado que termine el procesamiento
      if (this.invoicesListComponent) {
        this.invoicesListComponent.stopProcessing(item.id);
      }
    }
  }

  async confirmInvoice(invoice: any) {
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: {
          method: 'updateStatus',
          invoiceId: invoice.id,
          status: 'PENDING_CONFIRMED'
        }
      });

      if (response.success) {
        this.snackBar.open('Factura confirmada correctamente', 'Cerrar', { duration: 3000 });
        await this.loadAllData();
      } else {
        throw new Error(response.error || 'Error al confirmar la factura');
      }
    } catch (error) {
      console.error('Error confirming invoice:', error);
      this.snackBar.open('Error al confirmar la factura', 'Cerrar', { duration: 3000 });
    } finally {
      if (this.invoicesListComponent) {
        this.invoicesListComponent.stopProcessing(invoice.id);
      }
    }
  }

  async cancelInvoice(invoice: any) {
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: {
          method: 'updateStatus',
          invoiceId: invoice.id,
          status: 'CANCELLED'
        }
      });

      if (response.success) {
        this.snackBar.open('Factura cancelada correctamente', 'Cerrar', { duration: 3000 });
        await this.loadAllData();
      } else {
        throw new Error(response.error || 'Error al cancelar la factura');
      }
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      this.snackBar.open('Error al cancelar la factura', 'Cerrar', { duration: 3000 });
    } finally {
      if (this.invoicesListComponent) {
        this.invoicesListComponent.stopProcessing(invoice.id);
      }
    }
  }



  async copyRegistrationLink() {
    try {
      const registrationLink = `${window.location.origin}/onboarding/register?code=${this.partnerId}`;
      await navigator.clipboard.writeText(registrationLink);

      this.snackBar.open(
        this.translate.instant('BTB.DASHBOARD.REGISTRATION_LINK_COPIED'),
        'Cerrar',
        {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['success-snackbar']
        }
      );
    } catch (error) {
      console.error('Error copying registration link:', error);
      this.snackBar.open(
        this.translate.instant('BTB.DASHBOARD.ERROR_COPYING_LINK'),
        'Cerrar',
        {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        }
      );
    }
  }
}
